import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "date-fns";
import { de, enUS, fr, nl } from "date-fns/locale";

import {
  YearOverviewDialog,
  type YearOverviewResponse,
} from "../../reports/weeklyReport";
import type { DashboardMeasurement } from "../../../types/VesselGasoil";
import GasoilDialogForm from "./GasoilDialogForm";

type Props = {
  open: boolean;
  onClose: () => void;
  onMeasurementSaved?: () => Promise<void> | void;
  vesselId?: string | number | null;
  year?: number;
  allowAddMeasurement?: boolean;
};

const getDateFnsLocale = (language?: string): Locale => {
  const normalizedLanguage = String(language || "en").toLowerCase();

  if (normalizedLanguage.startsWith("nl")) return nl;
  if (normalizedLanguage.startsWith("fr")) return fr;
  if (normalizedLanguage.startsWith("de")) return de;

  return enUS;
};

const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_URL || "";
};

const GasoilYearOverviewDialog = ({
  open,
  onClose,
  onMeasurementSaved,
  vesselId,
  year = new Date().getFullYear(),
  allowAddMeasurement = true,
}: Props) => {
  const { t, i18n } = useTranslation();
  const locale = useMemo(
    () => getDateFnsLocale(i18n.language),
    [i18n.language]
  );
  const [overview, setOverview] = useState<YearOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [measurementDate, setMeasurementDate] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const initialMeasurement = useMemo<DashboardMeasurement | null>(() => {
    if (!measurementDate) {
      return null;
    }

    return {
      amount: null,
      date: `${measurementDate}T12:00:00`,
    };
  }, [measurementDate]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const abortController = new AbortController();

    const fetchOverview = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          year: String(year),
        });

        if (vesselId !== null && vesselId !== undefined && vesselId !== "") {
          params.set("vessel", String(vesselId));
        }

        const response = await window.fetch(
          `${getApiBaseUrl()}/reports/weekly-operations/year-overview?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "application/ld+json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          let responseMessage = "";

          try {
            const errorData = await response.json();
            responseMessage =
              errorData?.["hydra:description"] ||
              errorData?.detail ||
              errorData?.message ||
              "";
          } catch {
            responseMessage = "";
          }

          throw new Error(
            responseMessage ||
              response.statusText ||
              t(
                "failedToFetchYearOverview",
                "Failed to fetch year overview."
              )
          );
        }

        const data = (await response.json()) as YearOverviewResponse;

        if (!abortController.signal.aborted) {
          setOverview(data);
        }
      } catch (fetchError: any) {
        if (fetchError?.name !== "AbortError") {
          setError(
            fetchError?.message ||
              t(
                "failedToFetchYearOverview",
                "Failed to fetch year overview."
              )
          );
          setOverview(null);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void fetchOverview();

    return () => {
      abortController.abort();
    };
  }, [open, refreshVersion, t, vesselId, year]);

  const handleClose = () => {
    setMeasurementDate(null);
    onClose();
  };

  return (
    <>
      <YearOverviewDialog
        open={open}
        loading={loading}
        error={error}
        overview={overview}
        locale={locale}
        onAddMeasurement={
          allowAddMeasurement &&
          vesselId !== null &&
          vesselId !== undefined &&
          vesselId !== ""
            ? setMeasurementDate
            : undefined
        }
        onClose={handleClose}
        t={t}
      />

      <GasoilDialogForm
        open={Boolean(measurementDate)}
        vesselId={vesselId}
        initialData={initialMeasurement}
        onClose={() => setMeasurementDate(null)}
        onSaved={async () => {
          setRefreshVersion((current) => current + 1);
          await onMeasurementSaved?.();
        }}
      />
    </>
  );
};

export default GasoilYearOverviewDialog;
